import Image from 'next/future/image';
import React, { useCallback } from 'react';
import useSWR from 'swr';
import Link from 'next/link';
import cn from 'classnames';
import { Breakpoints } from '@/settings/breakpoints';
import { ReplacedProduct } from '@/lib/collections/catalogue/helpers';
import Section from '../commons/Section';
import { logger } from '@/lib/logger';
import { calculateSavings, getOfferUri } from '@/lib/offer';
import Slider from '../slider/Slider';
import { priceFormater } from '@/settings/index';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import useWindowSize from '@/lib/use-window-size';

const breakpoints = {
  [Breakpoints.NONE]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  [Breakpoints.XS]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  [Breakpoints.MD]: {
    slidesPerView: 2.5,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  [Breakpoints.LG]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 10,
  },
  [Breakpoints.XL]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 10,
  },
};

type ThemeT = {
  title: string;
  collectionHandle: string;
  randomize: boolean;
  pskus: string[];
  offers: ReplacedProduct[];
  collectionTitle: string;
  heroImage: any;
};

type ThemeHeroT = {
  isLeft: boolean;
  isEven: boolean;
  isMobile: boolean;
  collectionHandle: string;
};

const ThemeHero: React.FC<ThemeHeroT> = ({
  isLeft,
  isEven,
  isMobile,
  collectionHandle,
}) => {
  const shouldHide = useCallback(() => {
    if (isMobile) return !isLeft;
    if (isEven) return isLeft;

    return !isLeft;
  }, [isMobile, isEven, isLeft]);

  if (shouldHide()) return <></>;

  const edgeImageUrl = getEdgeUrl(
    collectionHandle === 'audio-home-entertainment'
      ? `images/category-themes/${collectionHandle}.gif`
      : `images/category-themes/${collectionHandle}.png`,
  );

  return (
    <Link href={`/collections/${collectionHandle}`} passHref>
      <div
        className={cn(
          'relative z-10 max-h-96 w-full  cursor-pointer overflow-hidden lg:w-1/3',
          { 'rounded-r-2xl': !isLeft },
          {
            'rounded-t-2xl lg:-mr-1 lg:rounded-l-2xl lg:rounded-r-none ':
              isLeft,
          },
        )}
      >
        <Image
          src={edgeImageUrl}
          className="w-full object-contain md:object-fill lg:h-full"
          alt="Category theme image"
          quality={100}
          width={440}
          height={440}
        />
      </div>
    </Link>
  );
};

const SliderContent: React.FC<{
  offers: ReplacedProduct[];
  index: number;
  title: string;
  collectionHandle: string;
  isMobile: boolean;
}> = ({ offers, index, collectionHandle, isMobile }) => {
  const isEven = index % 2 === 0;

  return (
    <div className="mt-6 flex flex-col  lg:flex-row">
      <ThemeHero
        isEven={isEven}
        isLeft={true}
        isMobile={isMobile}
        collectionHandle={collectionHandle}
      />
      <div
        className={cn(
          'relative flex w-full items-center rounded-b-2xl bg-gray-200 p-[10px] lg:w-2/3',
          { 'pr-2 lg:rounded-l-2xl lg:rounded-r-none': isEven },
          { 'pl-3 lg:rounded-r-2xl lg:rounded-l-none': !isEven },
        )}
      >
        <div className="w-full">
          <Slider breakpoints={breakpoints} speed={300}>
            {offers.map((offer, i) => {
              const percentageSaving = calculateSavings(
                offer.price,
                offer.comparePrice,
              );

              const shouldShowComparePrice =
                offer.comparePrice != null && percentageSaving > 3;

              return (
                <Link
                  href={getOfferUri(offer as any)}
                  passHref
                  key={offer.key + i}
                >
                  <div className="flex h-full w-full cursor-pointer flex-col gap-8 rounded-xl bg-white p-3">
                    <div className="flex items-center justify-center">
                      <div className="h-30 w-28">
                        <Image
                          width={112}
                          height={112}
                          quality={100}
                          alt={offer.title}
                          src={offer.imageSrc}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-2">
                      <h3
                        // eslint-disable-next-line tailwindcss/no-custom-classname
                        className="two-line-ellipsis text-sm font-light"
                      >
                        {offer.title}
                      </h3>
                      <h4 className="text-sm font-extrabold">
                        {priceFormater.long(offer.price)}
                      </h4>
                      {shouldShowComparePrice && (
                        <div className="text-xs text-gray-700/30">
                          new
                          <span className="line-through">
                            {` ${priceFormater.long(offer.comparePrice ?? '')}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </Slider>
        </div>
      </div>
      <ThemeHero
        isEven={isEven}
        isLeft={false}
        isMobile={isMobile}
        collectionHandle={collectionHandle}
      />
    </div>
  );
};

const CategoryThemeSlider: React.FC = () => {
  const { data, error, isValidating } = useSWR<{ themes: ThemeT[] }>(
    'api/collections/category-themes',
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const themes = data?.themes;

  if (error) logger.debug('Error fetching category themes', error);
  const windowSize = useWindowSize();

  const isMobile = windowSize[0] < 1024;

  if (isValidating) {
    return (
      <div
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className="hide-scrollbar flex items-center justify-center gap-5 overflow-x-scroll"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[268px] w-[284px] animate-pulse rounded-md !bg-[#E1E2E2]"
          />
        ))}
        <div className="h-[293px] w-[445px] animate-pulse rounded-md !bg-[#E1E2E2]" />
      </div>
    );
  }

  if (themes?.length) {
    return (
      <div className="bg-white">
        <Section>
          {themes.map(
            ({ collectionHandle, collectionTitle, title, offers }, i) => (
              <div
                key={`${title}_${collectionTitle}_${collectionHandle}_${i}`}
                className="reebelo-container mb-8"
              >
                {/* Subtitle & CTA */}
                <div className="flex w-full justify-between">
                  <h2 className="flex items-center text-lg font-semibold md:text-xl">
                    {title}
                  </h2>
                  <Link href={`/collections/${collectionHandle}`} passHref>
                    <a className="flex items-center gap-2 ">
                      <p className="text-xs font-extrabold lg:text-sm">
                        {isMobile ? 'See more' : `See all ${collectionTitle}`}
                      </p>
                      <div className="w-3 lg:block">
                        <svg viewBox="0 0 100 100">
                          <path
                            d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z"
                            transform="translate(100, 100) rotate(180) "
                          />
                        </svg>
                      </div>
                    </a>
                  </Link>
                </div>
                {/* Slider */}
                <SliderContent
                  offers={offers}
                  index={i}
                  title={title}
                  collectionHandle={collectionHandle}
                  isMobile={isMobile}
                />
              </div>
            ),
          )}
        </Section>
      </div>
    );
  }

  return null;
};

export default CategoryThemeSlider;
